/**
 * GIWAHS Adaptive Journey — Persisted Journey Record (AJIP-16.02.004 · WS3 · WS13)
 * ---------------------------------------------------------------------
 * Canonical persisted record schema. Only the fields listed here may be
 * written. Any attempt to include a forbidden key results in
 * ForbiddenFieldError (fail-closed).
 *
 * Required fields
 *   persistenceRecordId    string (uuid-shaped)
 *   journeyInstanceId      string (opaque)
 *   journeyDefinitionId    string (matches an entry in JourneyRegistry)
 *   journeyType            string (opaque type tag)
 *   currentState           string (one of ALL_STATES)
 *   currentStage           string | null
 *   progress               number in [0, 1]
 *   authorisedData         object  (framework-opaque; forbidden keys blocked)
 *   schemaVersion          integer >= 1
 *   createdAt              ISO string
 *   updatedAt              ISO string
 *   expiresAt              ISO string
 *   ownerRef               string | null   (opaque owner reference)
 *   revision               integer >= 1
 *   integrity              { algorithm, checksum }
 *   restorationStatus      one of RESTORATION_STATUSES
 *
 * Forbidden keys inside authorisedData or anywhere in the record.
 * Governance rule (WS3 + WS13): secrets and credentials are never
 * persisted.
 */
import { ALL_STATES } from '../core/JourneyState';
import { fnv1a, CHECKSUM_ALGORITHM } from './Hash';
import {
  SchemaValidationError,
  ForbiddenFieldError,
  PayloadTooLargeError,
  IntegrityError,
  RecordCorruptError,
} from './PersistenceErrors';

const CURRENT_SCHEMA_VERSION = 1;
const MAX_SERIALISED_BYTES = 64 * 1024; // 64 KB hard cap

const RESTORATION_STATUSES = Object.freeze([
  'PENDING',
  'RESTORED',
  'FAILED',
  'EXPIRED',
]);

// Forbidden top-level and nested keys. Case-insensitive check.
const FORBIDDEN_KEYS = Object.freeze([
  'password',
  'passphrase',
  'token',
  'accessToken',
  'refreshToken',
  'apiKey',
  'secret',
  'credential',
  'credentials',
  'cardNumber',
  'cvv',
  'cvc',
  'ssn',
  'authHeader',
  'authorization',
  'cookie',
  'sessionId',
  'privateKey',
]);

const forbiddenKeyLc = new Set(FORBIDDEN_KEYS.map((k) => k.toLowerCase()));

const REQUIRED_FIELDS = Object.freeze([
  'persistenceRecordId',
  'journeyInstanceId',
  'journeyDefinitionId',
  'journeyType',
  'currentState',
  'currentStage',
  'progress',
  'authorisedData',
  'schemaVersion',
  'createdAt',
  'updatedAt',
  'expiresAt',
  'ownerRef',
  'revision',
  'integrity',
  'restorationStatus',
]);

const isIsoString = (v) => typeof v === 'string' && !Number.isNaN(Date.parse(v));
const isUuidShape = (v) => typeof v === 'string' && v.length >= 8 && v.length <= 128;

const walkForForbiddenKeys = (value, path = 'authorisedData') => {
  if (value === null || value === undefined) return;
  if (typeof value !== 'object') return;
  if (Array.isArray(value)) {
    value.forEach((item, i) => walkForForbiddenKeys(item, `${path}[${i}]`));
    return;
  }
  for (const [k, v] of Object.entries(value)) {
    if (forbiddenKeyLc.has(k.toLowerCase())) {
      throw new ForbiddenFieldError(`Forbidden key '${k}' at ${path}`);
    }
    walkForForbiddenKeys(v, `${path}.${k}`);
  }
};

const assertPlainObject = (value, name) => {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    throw new SchemaValidationError(`${name} must be a plain object`);
  }
  // Prototype pollution guard.
  if (Object.getPrototypeOf(value) !== Object.prototype && Object.getPrototypeOf(value) !== null) {
    throw new SchemaValidationError(`${name} must have a plain prototype`);
  }
};

const validateRecord = (record) => {
  if (!record || typeof record !== 'object') {
    throw new SchemaValidationError('Record must be a non-null object');
  }
  for (const f of REQUIRED_FIELDS) {
    if (!(f in record)) throw new SchemaValidationError(`Missing required field: ${f}`);
  }
  if (!isUuidShape(record.persistenceRecordId)) {
    throw new SchemaValidationError('persistenceRecordId must be a string of 8..128 chars');
  }
  if (typeof record.journeyInstanceId !== 'string' || record.journeyInstanceId.length === 0) {
    throw new SchemaValidationError('journeyInstanceId must be a non-empty string');
  }
  if (typeof record.journeyDefinitionId !== 'string' || record.journeyDefinitionId.length === 0) {
    throw new SchemaValidationError('journeyDefinitionId must be a non-empty string');
  }
  if (typeof record.journeyType !== 'string') {
    throw new SchemaValidationError('journeyType must be a string');
  }
  if (!ALL_STATES.includes(record.currentState)) {
    throw new SchemaValidationError(`currentState '${record.currentState}' is not a valid state`);
  }
  if (record.currentStage !== null && typeof record.currentStage !== 'string') {
    throw new SchemaValidationError('currentStage must be string or null');
  }
  if (typeof record.progress !== 'number' || Number.isNaN(record.progress) || record.progress < 0 || record.progress > 1) {
    throw new SchemaValidationError('progress must be a number in [0, 1]');
  }
  assertPlainObject(record.authorisedData, 'authorisedData');
  walkForForbiddenKeys(record.authorisedData);
  if (!Number.isInteger(record.schemaVersion) || record.schemaVersion < 1) {
    throw new SchemaValidationError('schemaVersion must be an integer >= 1');
  }
  if (!isIsoString(record.createdAt)) throw new SchemaValidationError('createdAt must be an ISO string');
  if (!isIsoString(record.updatedAt)) throw new SchemaValidationError('updatedAt must be an ISO string');
  if (!isIsoString(record.expiresAt)) throw new SchemaValidationError('expiresAt must be an ISO string');
  if (record.ownerRef !== null && typeof record.ownerRef !== 'string') {
    throw new SchemaValidationError('ownerRef must be string or null');
  }
  if (!Number.isInteger(record.revision) || record.revision < 1) {
    throw new SchemaValidationError('revision must be an integer >= 1');
  }
  if (!record.integrity || typeof record.integrity !== 'object') {
    throw new SchemaValidationError('integrity must be an object');
  }
  if (record.integrity.algorithm !== CHECKSUM_ALGORITHM) {
    throw new SchemaValidationError(`integrity.algorithm must be '${CHECKSUM_ALGORITHM}'`);
  }
  if (typeof record.integrity.checksum !== 'string' || record.integrity.checksum.length === 0) {
    throw new SchemaValidationError('integrity.checksum must be a non-empty string');
  }
  if (!RESTORATION_STATUSES.includes(record.restorationStatus)) {
    throw new SchemaValidationError(`restorationStatus '${record.restorationStatus}' is invalid`);
  }
  return true;
};

// Fields covered by the checksum. Excludes the integrity block itself.
const PAYLOAD_FIELDS = Object.freeze(
  REQUIRED_FIELDS.filter((f) => f !== 'integrity' && f !== 'restorationStatus'),
);

const computeChecksum = (record) => {
  const payload = {};
  for (const f of PAYLOAD_FIELDS) payload[f] = record[f];
  return fnv1a(JSON.stringify(payload));
};

const withChecksum = (record) => ({
  ...record,
  integrity: { algorithm: CHECKSUM_ALGORITHM, checksum: computeChecksum(record) },
});

const verifyChecksum = (record) => {
  if (!record || !record.integrity || typeof record.integrity.checksum !== 'string') {
    throw new IntegrityError('Record has no integrity block');
  }
  const expected = computeChecksum(record);
  if (expected !== record.integrity.checksum) {
    throw new IntegrityError('Checksum mismatch — record may be corrupt', {
      expected,
      actual: record.integrity.checksum,
    });
  }
  return true;
};

const serialise = (record) => {
  validateRecord(record);
  const s = JSON.stringify(record);
  if (typeof s !== 'string') throw new SchemaValidationError('Record failed to serialise');
  if (Buffer.byteLength(s, 'utf8') > MAX_SERIALISED_BYTES) {
    throw new PayloadTooLargeError(`Payload exceeds ${MAX_SERIALISED_BYTES} bytes`);
  }
  return s;
};

const deserialise = (input) => {
  if (typeof input !== 'string') throw new RecordCorruptError('Persisted value must be a string');
  if (Buffer.byteLength(input, 'utf8') > MAX_SERIALISED_BYTES) {
    throw new PayloadTooLargeError(`Persisted payload exceeds ${MAX_SERIALISED_BYTES} bytes`);
  }
  let parsed;
  try {
    parsed = JSON.parse(input);
  } catch (_e) {
    throw new RecordCorruptError('Persisted value is not valid JSON');
  }
  // Reject anything that is not a plain object at the top level.
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new RecordCorruptError('Persisted value is not a plain object');
  }
  // Strict validation before we do anything else.
  validateRecord(parsed);
  return parsed;
};

const createRecord = ({
  persistenceRecordId,
  journeyInstanceId,
  journeyDefinitionId,
  journeyType,
  currentState,
  currentStage = null,
  progress = 0,
  authorisedData = {},
  createdAt,
  updatedAt,
  expiresAt,
  ownerRef = null,
  revision = 1,
  restorationStatus = 'PENDING',
}) => {
  const draft = {
    persistenceRecordId,
    journeyInstanceId,
    journeyDefinitionId,
    journeyType,
    currentState,
    currentStage,
    progress,
    authorisedData,
    schemaVersion: CURRENT_SCHEMA_VERSION,
    createdAt,
    updatedAt,
    expiresAt,
    ownerRef,
    revision,
    restorationStatus,
    // Placeholder integrity block so validateRecord() accepts the draft
    // for its structural check; the real checksum is applied below.
    integrity: { algorithm: CHECKSUM_ALGORITHM, checksum: '00000000' },
  };
  validateRecord(draft);
  return withChecksum(draft);
};

export {
  CURRENT_SCHEMA_VERSION,
  MAX_SERIALISED_BYTES,
  REQUIRED_FIELDS,
  RESTORATION_STATUSES,
  FORBIDDEN_KEYS,
  PAYLOAD_FIELDS,
  validateRecord,
  computeChecksum,
  withChecksum,
  verifyChecksum,
  serialise,
  deserialise,
  createRecord,
};
